import React, { useState } from 'react';
import { LOFTEN_TEXT } from '../../lib/loften';
import { useSearchParams } from 'react-router-dom';
import Nav from '../../components/Nav';
import Footer from '../../components/Footer';
import {
  Page, Section, Inner,
  Eyebrow, EyebrowDot, EyebrowText,
  Headline, Sub,

  Card, CardHeadline, CardSub,
  OAuthBtn, OAuthBadge, OAuthLabel, OAuthArrow,
  Divider,
  EmailRow, EmailInput, SubmitBtn, ErrorMsg, Privacy,
  SuccessWrap, CheckRing, SuccessHeadline, SuccessSub, SuccessEmail, UpgradeLabel,
  NextStrip, NextItem, NextNum, NextText,
  RulesWrap, Rule,
} from './styles';

const fmt = n => new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 }).format(n);

function CheckIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28">
      <polyline points="5,14 11,20 23,8" />
    </svg>
  );
}

export default function Aktivera() {
  const [params] = useSearchParams();
  // Här lästes «?savings=» ur URL:en och visades som «Vi identifierade redan X kr/år» — och skickades
  // vidare till aktiveringen som kundens besparing. Ingen kod bygger en sådan länk; talet kunde bara
  // komma ur en handskriven URL. Ett tal vi inte räknat visas aldrig (kundmeningsregistret, regel 3).
  const savings  = null;
  const supplier = params.get('supplier') ?? null;
  const score    = params.get('score')    ? Number(params.get('score'))   : null;

  const [email, setEmail]     = useState('');
  const [status, setStatus]   = useState('idle'); // idle | submitting | sent | error
  const [errMsg, setErrMsg]   = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || status === 'submitting') return;
    setStatus('submitting');
    setErrMsg('');

    try {
      const res = await fetch('/api/activate-intelligence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmed,
          supplier:  supplier ?? undefined,
          netSaving: savings  ?? undefined,
          diagScore: score    ?? undefined,
          source:    'intelligence-page',
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error ?? 'server_error');
      }
      setStatus('sent');
    } catch (err) {
      setErrMsg('Något gick fel — försök igen.');
      setStatus('error');
    }
  };

  const outlookUrl = `/api/auth/outlook-init${email ? `?email=${encodeURIComponent(email)}` : ''}`;

  return (
    <Page>
      <Nav variant="public" />

      <Section>
        <Inner>

          <Eyebrow>
            <EyebrowDot />
            <EyebrowText>Arvo Intelligence</EyebrowText>
          </Eyebrow>

          {status !== 'sent' && (
            <>
              <Headline>
                Låt Arvo bevaka<br />era leverantörer.
              </Headline>
              <Sub>1&nbsp;995&nbsp;kr/mån &middot; Ingen bindningstid</Sub>
            </>
          )}

          <Card>
            {status === 'sent' ? (
              /* ── Success ── */
              <SuccessWrap>
                <CheckRing><CheckIcon /></CheckRing>
                <SuccessHeadline>Anmälan mottagen.</SuccessHeadline>
                <SuccessSub>{LOFTEN_TEXT.intelligenceAnmalan}</SuccessSub>
                <SuccessEmail>{email}</SuccessEmail>

                <UpgradeLabel>Koppla er inkorg</UpgradeLabel>
                <OAuthBtn href={outlookUrl}>
                  <OAuthBadge $provider="outlook">&#9632;</OAuthBadge>
                  <OAuthLabel>Koppla Outlook</OAuthLabel>
                  <OAuthArrow>→</OAuthArrow>
                </OAuthBtn>
                <Privacy>
                  Arvo läser bara faktura-mail — aldrig personlig korrespondens.
                </Privacy>
              </SuccessWrap>
            ) : (
              /* ── Default ── */
              <>
                <CardHeadline>Koppla er inkorg — en gång.</CardHeadline>
                <CardSub>{LOFTEN_TEXT.inkorgskoppling}</CardSub>

                <OAuthBtn href={outlookUrl}>
                  <OAuthBadge $provider="outlook">&#9632;</OAuthBadge>
                  <OAuthLabel>Koppla Outlook</OAuthLabel>
                  <OAuthArrow>→</OAuthArrow>
                </OAuthBtn>

                <Divider><span>eller börja med e-post</span></Divider>

                <EmailRow onSubmit={handleSubmit}>
                  <EmailInput
                    type="email"
                    placeholder="er@foretag.se"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                  <SubmitBtn type="submit" disabled={status === 'submitting'}>
                    {status === 'submitting' ? '…' : 'Anmäl intresse →'}
                  </SubmitBtn>
                </EmailRow>
                {errMsg && <ErrorMsg>{errMsg}</ErrorMsg>}

                <Privacy>
                  1&nbsp;995&nbsp;kr/mån &middot; Ingen bindningstid &middot; Arvo läser bara faktura-mail, aldrig personlig korrespondens.
                </Privacy>
              </>
            )}
          </Card>

          <NextStrip>
            <NextItem>
              <NextNum>24h</NextNum>
              <NextText>Arvo aktiverar er bevakning</NextText>
            </NextItem>
            <NextItem>
              <NextNum>Dag 7</NextNum>
              <NextText>Ni får er första analys</NextText>
            </NextItem>
            <NextItem>
              <NextNum>Löpande</NextNum>
              <NextText>Arvo kontaktar er om något hänt</NextText>
            </NextItem>
          </NextStrip>

        </Inner>
      </Section>

      <RulesWrap>
        <Rule><strong>Regel 1:</strong> Arvo vaktar er för 1&nbsp;995&nbsp;kr/mån.</Rule>
        <Rule><strong>Regel 2:</strong> Ni behåller 80% av allt vi sparar er.</Rule>
      </RulesWrap>

      <Footer />
    </Page>
  );
}
